import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useRouter} from 'next/router';
import {type Dispatch, type SetStateAction, useState} from 'react';
import {MAIL_AUTH_SENT} from '../../../config/constants/routes';
import {
  EMAIL_INPUT_ERROR_TYPE,
  type EmailInputErrorType,
} from '../../../core/entities/register/constants';
import {
  useGetIntroductionCampaignMessage,
  usePostRegisterAcMailAuth,
} from '../../../core/repositories/registerRepository';
import {resolvePromotionCampaignCode} from '../../../core/usecases/promotionCampaign/promotionCampaignCode';
import type {NextPageWithLayout} from '../../../pages/_app';
import {
  emailRegex,
  nonMicrosoftMailRegex,
} from '../../../shared/services/emailValidation';
import {MailAuth} from './Container/Presenter/MailAuthPresenter';

export type Props = {
  emailInputErrorType: EmailInputErrorType;
  setEmailInputErrorType: Dispatch<SetStateAction<EmailInputErrorType>>;
  inputEmail: string;
  setInputEmail: Dispatch<SetStateAction<string>>;
  handleRegisterClick: () => void;
  introduceCampaignMessage: string;
  introduceId: string;
  campaignId: string;
  isSubmitLoading: boolean;
};

type CastedError = {message: AppError};

export const MailAuthPage: NextPageWithLayout = () => {
  const [emailInputErrorType, setEmailInputErrorType] =
    useState<EmailInputErrorType>('');
  const [inputEmail, setInputEmail] = useState<string>('');

  const router = useRouter();
  const introduceId = router.query.introducer_id
    ? String(router.query.introducer_id)
    : '';
  const campaignId = router.query.campaign_id
    ? String(router.query.campaign_id)
    : '';

  const {data, isLoading} = useGetIntroductionCampaignMessage({
    campaignId,
  });

  const [isSubmitLoading, {open, close}] = useDisclosure(false);

  const postRegisterAcMail = usePostRegisterAcMailAuth({
    onSuccessFunc() {
      // レスポンスが成功なら、メール送信完了画面に遷移
      // ストレージが使えない環境でも完了画面への遷移は止めない（アドレス表示が省略されるだけ）
      try {
        sessionStorage.setItem('mailAuthSentEmail', inputEmail);
      } catch {}
      location.href = MAIL_AUTH_SENT;
    },
    onErrorFunc(error: unknown) {
      const castedError = error as CastedError;
      close();
      if (castedError.message === 'ConflictError') {
        setEmailInputErrorType(EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR);
      } else {
        setEmailInputErrorType(EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR);
      }
    },
  });

  const handleRegisterClick = () => {
    open();
    // バリデーション
    if (emailInputErrorType) {
      close();
      return;
    }

    if (!emailRegex.test(inputEmail)) {
      close();
      setEmailInputErrorType(EMAIL_INPUT_ERROR_TYPE.FORMAT_ERROR);
      return;
    }

    if (!nonMicrosoftMailRegex.test(inputEmail)) {
      close();
      setEmailInputErrorType(EMAIL_INPUT_ERROR_TYPE.MICROSOFT_ERROR);
      return;
    }

    const referer = window?.document?.referrer ?? '';
    const inflowPath = router.query.inflowPath
      ? String(router.query.inflowPath)
      : (referer ?? '');
    // 期限切れリマインドメールから来た場合のみ、URL の rk(暗号化された送信ログID・計測用)が付与される
    const rk = router.query.rk ? String(router.query.rk) : undefined;
    // プロモーションキャンペーンコードはURLクエリを優先し、無ければCookie(gsskt_pcid)をフォールバックとして読む
    const promotionCampaignCode = resolvePromotionCampaignCode(router.query);
    // メール送信APIを叩く
    postRegisterAcMail.mutate({
      requestBody: {
        email: inputEmail,
        registerType: 0, // TODO: 元々は使われていたが、現在は使われていないので、削除したい
        introducerID: introduceId || '',
        userIntroduceCampaignID: Number(campaignId) || 0,
        inflowPath,
        rk,
        promotionCampaignCode,
      },
    });
  };

  const props: Props = {
    emailInputErrorType,
    setEmailInputErrorType,
    inputEmail,
    setInputEmail,
    handleRegisterClick,
    introduceCampaignMessage: data?.ok ? data.value.message : '',
    introduceId,
    campaignId,
    isSubmitLoading,
  };

  return <>{!isLoading && <MailAuth {...props} />}</>;
};
